import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { httpClient } from "@coworker/http-client";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { getEnvironmentId } from "@coworker/reusable/helpers/environment";
import { useQuery } from "@tanstack/react-query";
import { useGetInsightsAPIKey } from "./fetch-insights-api-key";

type InsightsDataFilters = {
  itemNo?: string;
  refillAreaId?: string;
  itemRefillLocationGridCode?: string;
  itemHfbNo?: string;
  selectedPeriod: string;
};

export type InsightsTablesType =
  | "articleInsights"
  | "refilledAreaInsights"
  | "refilledSalesLocationIDInsights"
  | "itemHfbTypeInsights";

export type InsightsOrderByColumn =
  | "estimatedTurnover"
  | "pieces"
  | "numberOfRefillLocations"
  | "numberOfArticles";

export type InsightsOrderByValues = {
  column: InsightsOrderByColumn;
  direction: "asc" | "desc";
};

export type InsightsOrderByColumns = {
  [table in InsightsTablesType]: InsightsOrderByValues;
};

type InsightsTablePages = {
  [table in InsightsTablesType]: number;
};

type InsightsEndpoints =
  | "articles"
  | "refill_areas"
  | "refilled_sales_location_ids"
  | "item_hfb_types"
  | "total_counts";

const getInsightsEndpointUrl = (
  storeId: string,
  endpoint: InsightsEndpoints,
  filters: InsightsDataFilters,
  pageParam: number,
  orderByValue?: string
) => {
  const pageSize = 5;

  let url =
    endpoint === "total_counts"
      ? `insights/total_counts?storeId=${encodeURIComponent(storeId)}&selectedPeriod=${encodeURIComponent(filters.selectedPeriod)}&`
      : `insights/${endpoint}?storeId=${encodeURIComponent(storeId)}&selectedPeriod=${encodeURIComponent(filters.selectedPeriod)}${orderByValue}&pageSize=${pageSize}&page=${pageParam}&`;

  for (const key in filters) {
    if (key === "selectedPeriod") continue; // already added above — required field must never be skipped
    const value = filters[key as keyof InsightsDataFilters];

    if (!value) {
      continue;
    }

    url += `${key}=${encodeURIComponent(value)}&`;
  }

  return url;
};

// @google-cloud/bigquery approach is not supported in China, so we use direct API endpoints for Chinese users instead
const chinaInsightsEndpoint = () => {
  const environmentId = getEnvironmentId();
  if (environmentId === "dev") {
    return "https://api.dev.fixa.ingka.com/eu-w1/tasks-service";
  } else if (environmentId === "prod") {
    return "https://api.fixa.ingka.com/as-e1/tasks-service";
  } else {
    console.error(
      `Unknown environmentId '${environmentId}' in chinaInsightsEndpoint, defaulting to dev endpoint.`
    );
    return "https://api.dev.fixa.ingka.com/eu-w1/tasks-service";
  }
};

const useInsightsBaseUrl = () => {
  return isChineseEnvironment() ? chinaInsightsEndpoint() : "";
};

const fetchInsights = async (
  storeId: string,
  endpoint: InsightsEndpoints,
  filters: InsightsDataFilters,
  pageParam: number,
  baseUrl: string,
  apiKey: string,
  orderByColumn?: string
) => {
  const path = `/${getInsightsEndpointUrl(storeId, endpoint, filters, pageParam, orderByColumn)}`;

  let data: unknown;
  if (baseUrl) {
    // China environment uses absolute URL with API-key auth (routes to Asia env)
    const response = await fetch(`${baseUrl}${path}`, {
      headers: {
        "X-Api-Key": apiKey,
      },
    });
    if (!response.ok) {
      throw new Error(`Insights fetch failed: ${response.status}`);
    }
    data = await response.json();
  } else {
    data = await httpClient.tasks.get<unknown>(path, {
      headers: {
        "X-Api-Key": apiKey,
      },
    });
  }

  if (endpoint === "total_counts" && !!data) {
    return data;
  }

  if (data) {
    return { ...(data as object), pageParam };
  }

  return { rows: [], totalRowCount: 0, pageParam };
};

export const useInsightsData = (
  tablePages: InsightsTablePages,
  filters: InsightsDataFilters,
  orderByColumn: InsightsOrderByColumns
) => {
  const storeId = useStoreId();
  const baseUrl = useInsightsBaseUrl();
  const { data: apiKey } = useGetInsightsAPIKey();
  const filtersIndex = [];
  for (const key in filters) {
    if (filters[key as keyof InsightsDataFilters]) {
      filtersIndex.push(`${key}=${filters[key as keyof InsightsDataFilters]}`);
    }
  }
  filtersIndex.sort();

  const orderByValue = (orderBy: {
    column: InsightsOrderByColumn;
    direction: "asc" | "desc";
  }) => {
    if (!orderBy) return "";
    return `&orderByColumn=${orderBy.column}&orderDirection=${orderBy.direction}`;
  };

  const articleInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "articleInsights",
      tablePages.articleInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.articleInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "articles",
        filters,
        tablePages.articleInsights,
        baseUrl,
        apiKey as string,
        orderByValue(orderByColumn.articleInsights)
      );
    },
    enabled: !!apiKey && !!filters.selectedPeriod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const refilledAreaInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "refilledAreaInsights",
      tablePages.refilledAreaInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.refilledAreaInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "refill_areas",
        filters,
        tablePages.refilledAreaInsights,
        baseUrl,
        apiKey as string,
        orderByValue(orderByColumn.refilledAreaInsights)
      );
    },
    enabled: !!apiKey && !!filters.selectedPeriod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const refilledSalesLocationIDInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "refilledSalesLocationIDInsights",
      tablePages.refilledSalesLocationIDInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.refilledSalesLocationIDInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "refilled_sales_location_ids",
        filters,
        tablePages.refilledSalesLocationIDInsights,
        baseUrl,
        apiKey as string,
        orderByValue(orderByColumn.refilledSalesLocationIDInsights)
      );
    },
    enabled: !!apiKey && !!filters.selectedPeriod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const itemHfbTypeInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "itemHfbTypeInsights",
      tablePages.itemHfbTypeInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.itemHfbTypeInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "item_hfb_types",
        filters,
        tablePages.itemHfbTypeInsights,
        baseUrl,
        apiKey as string,
        orderByValue(orderByColumn.itemHfbTypeInsights)
      );
    },
    enabled: !!apiKey && !!filters.selectedPeriod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const totalCounts = useQuery<{ pieces?: string; estimatedTurnover?: string }>(
    {
      queryKey: ["insights", "totalCounts", ...filtersIndex],
      queryFn: async () =>
        (await fetchInsights(
          storeId,
          "total_counts",
          filters,
          1,
          baseUrl,
          apiKey as string
        )) as {
          pieces?: string;
          estimatedTurnover?: string;
        },
      enabled: !!apiKey && !!filters.selectedPeriod,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  return {
    articleInsights: articleInsightsQueryResult,
    refilledAreaInsights: refilledAreaInsightsQueryResult,
    refilledSalesLocationIDInsights: refilledSalesLocationIDInsightsQueryResult,
    itemHfbTypeInsights: itemHfbTypeInsightsQueryResult,
    totalCounts: totalCounts,
  };
};
